import React, { useState, useCallback, useRef, useEffect, forwardRef } from 'react';
import DOMPurify from 'dompurify';
import debounce from 'lodash/debounce';
import './PostReplyInput.css';
import { UserType } from '../../../../types/userType';
import { detectLink, convertLinkToTag } from '../../../common/utils';
import { getLinkPreview, LinkPreviewData } from '../../../../services/linkPreviewService';
import LinkPreview from './LinkPreview';
import icon_newpost_delete from '../../../../assets/icons/icon_newpost_delete.svg';
import icon_newpost_images from '../../../../assets/icons/icon_newpost_images.svg';
import icon_newpost_camera from '../../../../assets/icons/icon_newpost_camera.svg';
import icon_newpost_gif from '../../../../assets/icons/icon_newpost_gif.svg';
import GiphyModal from '../../../common/GiphyModal/GiphyModal';
import Camera from '../../../common/Camera/Camera';
import VIToast from '../../../common/VIToast/VIToast';

// Define a type for any post that can be replied to (original post or reply)
interface ReplyTarget {
  id: string | number;
  username: string;
  isReply: boolean;
  parentId?: string | number | null; // Add null as a valid type
}

interface PostReplyInputProps {
  content: string;
  onContentChange: (newText: string) => void;
  currentUser: UserType | null;
  originalPostUsername: string;
  placeholder?: string;
  isClickable?: boolean;
  replyTo?: ReplyTarget; // Information about the post being replied to
  onSubmit?: (text: string, images: File[], gif: string | null, replyToId: string | number) => Promise<void>;
}

const PostReplyInput = forwardRef<HTMLTextAreaElement, PostReplyInputProps>(
  ({ 
    content, 
    onContentChange, 
    currentUser, 
    originalPostUsername, 
    placeholder, 
    isClickable = false,
    replyTo,
    onSubmit
  }, ref) => {
    const [text, setText] = useState(content);
    const [htmlContent, setHtmlContent] = useState('');
    const [images, setImages] = useState<string[]>([]);
    const [linkPreview, setLinkPreview] = useState<LinkPreviewData | null>(null);
    const [selectedGif, setSelectedGif] = useState<string | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [showGiphyModal, setShowGiphyModal] = useState(false);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }, []);

    const debouncedDetectLink = useCallback(
      debounce(async (text: string): Promise<string | null> => {
        const link = detectLink(text);
        if (link) {
          try {
            const previewData = await getLinkPreview(link);
            setLinkPreview(previewData ? previewData as LinkPreviewData : null);
            return link;
          } catch (error) {
            console.error('Error fetching link preview:', error);
            setLinkPreview(null);
          }
        } else {
          setLinkPreview(null);
        }
        return null;
      }, 1000),
      []
    );

    const handleTextChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newText = e.target.value;
      if (newText.length <= 1000) {
        setText(newText);
        onContentChange(newText);
        const sanitizedText = DOMPurify.sanitize(newText);

        const detectedLink = await debouncedDetectLink(newText);

        let contentWithLinks = sanitizedText;
        if (detectedLink) {
          contentWithLinks = convertLinkToTag(detectedLink);
        }

        setHtmlContent(contentWithLinks);
      } else {
        setToastMessage('Reply is limited to 1000 characters.');
      }
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files) {
        if (images.length + files.length + (selectedGif ? 1 : 0) > 10) {
          setToastMessage('You can only add up to 10 media items (images + GIF).');
          return;
        }
        const newImages = Array.from(files).map(file => URL.createObjectURL(file));
        setImages([...images, ...newImages]);
      }
    };

    const handleCameraCapture = () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setToastMessage('Camera not available on this device.');
        return;
      }
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(() => {
          setShowCamera(true);
        })
        .catch((error) => {
          console.error('Error accessing the camera:', error);
          setToastMessage('Error accessing the camera. Please check your device settings.');
        });
    };

    const handlePhotoCapture = (imageSrc: string) => {
      setImages([...images, imageSrc]);
      setShowCamera(false);
    };

    const handleCloseCamera = () => {
      setShowCamera(false);
    };

    const handleGifClick = () => {
      setShowGiphyModal(true);
    };

    const handleGifSelect = (gifUrl: string) => {
      if (images.length + (selectedGif ? 1 : 0) >= 10) {
        setToastMessage('You can only add up to 10 media items (images + GIF).');
        setShowGiphyModal(false);
        return;
      }
      setSelectedGif(gifUrl);
      setShowGiphyModal(false);
    };

    const handleGiphyModalClose = () => {
      setShowGiphyModal(false);
    };

    const handleDelete = () => {
      setText('');
      setHtmlContent('');
      setImages([]);
      setLinkPreview(null);
      setSelectedGif(null);
      if (textAreaRef.current) {
        textAreaRef.current.value = '';
      }
    };

    const handleImageDelete = (index: number) => {
      const newImages = [...images];
      newImages.splice(index, 1);
      setImages(newImages);
    };

    const handleGifDelete = () => {
      setSelectedGif(null);
    };

    const handleLinkPreviewDelete = () => {
      setLinkPreview(null);
      const updatedText = text.replace(linkPreview?.url || '', '');
      setText(updatedText);
      setLinkPreview(null);
      setHtmlContent("");
    };

    // Determine the placeholder text based on what's being replied to
    const getPlaceholderText = () => {
      if (placeholder) return placeholder;
      
      if (replyTo) {
        return `Reply to ${replyTo.username}...`;
      }
      
      return `Reply to ${originalPostUsername}...`;
    };

    const handleSubmit = async () => {
      if (text.trim() === '' && images.length === 0 && !selectedGif) {
        setToastMessage('Please add some content to your reply.');
        return;
      }

      if (!onSubmit) {
        console.warn('No onSubmit handler provided to PostReplyInput');
        return;
      }

      try {
        setIsSubmitting(true);
        
        const imageFiles = await Promise.all(
          images.map(async (imageSrc) => {
            const response = await fetch(imageSrc);
            const blob = await response.blob();
            return new File([blob], 'image.jpg', { type: 'image/jpeg' });
          })
        );

        // Call the onSubmit handler with the reply target ID
        await onSubmit(
          text, 
          imageFiles, 
          selectedGif, 
          replyTo?.id || 0 // Use the ID of what we're replying to
        );

        // Clear the input after successful submission
        handleDelete();
      } catch (error) {
        console.error('Error submitting reply:', error);
        setToastMessage('Failed to submit reply. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    // Calculate CSS classes based on whether this is replying to a reply
    const getContainerClasses = () => {
      const classes = ['post-reply-input'];
      
      if (isClickable) {
        classes.push('clickable');
      }
      
      if (replyTo?.isReply) {
        classes.push('reply-to-reply');
        
        // If replying to a reply of a reply, add deeper indentation
        if (replyTo.parentId) {
          classes.push('nested-reply');
        }
      }
      
      return classes.join(' ');
    };

    return (
      <div className={getContainerClasses()}>
        {replyTo && (
          <div className="reply-to-indicator">
            Replying to <span className="reply-username">{replyTo.username}</span>
          </div>
        )}
        <div className="post-reply-avatar-column">
          <img src={currentUser?.avatar} alt={currentUser?.username} className="post-reply-avatar" />
        </div>
        <div className="post-reply-content-column">
          <div className="post-reply-username">{currentUser?.username}</div>
          <textarea
            ref={ref || textAreaRef}
            value={text}
            onChange={handleTextChange}
            placeholder={getPlaceholderText()}
            className="post-reply-textarea"
            readOnly={isClickable}
            style={{ cursor: isClickable ? 'pointer' : 'text' }}
          />

          {/* Display media content (images, GIFs, link previews) */}
          {(images.length > 0 || selectedGif || linkPreview) && (
            <div className="post-reply-media">
              {/* Display uploaded images */}
              {images.length > 0 && (
                <div className="uploaded-images">
                  {images.map((image, index) => (
                    <div key={index} className="uploaded-image-container">
                      <img src={image} alt={`Uploaded ${index}`} className="uploaded-image" />
                      <button
                        className="image-delete-button"
                        onClick={() => handleImageDelete(index)}
                      >
                        <img src={icon_newpost_delete} alt="Delete" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Display selected GIF */}
              {selectedGif && (
                <div className="selected-gif-container">
                  <img src={selectedGif} alt="Selected GIF" className="selected-gif" />
                  <button
                    className="gif-delete-button"
                    onClick={handleGifDelete}
                  >
                    <img src={icon_newpost_delete} alt="Delete" />
                  </button>
                </div>
              )}

            </div>
          )}

          {/* Media upload options */}
          <div className="post-reply-actions">
            <label className="media-upload-button">
              <img src={icon_newpost_images} alt="Upload images" />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
            
            <button className="media-upload-button" onClick={handleCameraCapture}>
              <img src={icon_newpost_camera} alt="Take photo" />
            </button>
            
            <button className="media-upload-button" onClick={handleGifClick}>
              <img src={icon_newpost_gif} alt="Add GIF" />
            </button>
            
            <button 
              className="reply-submit-button"
              onClick={handleSubmit}
              disabled={isSubmitting || (text.trim() === '' && images.length === 0 && !selectedGif)}
            >
              {isSubmitting ? 'Sending...' : 'Reply'}
            </button>
          </div>
        </div>

        {/* Modals */}
        {showGiphyModal && (
          <GiphyModal
            isOpen={showGiphyModal}
            onSelect={handleGifSelect}
            onClose={handleGiphyModalClose}
          />
        )}
        
        {showCamera && (
          <Camera
            onCapture={handlePhotoCapture}
            onClose={handleCloseCamera}
          />
        )}
        
        {toastMessage && (
          <VIToast message={toastMessage} onClose={() => setToastMessage(null)} />
        )}
      </div>
    );
  }
);

PostReplyInput.displayName = 'PostReplyInput';

export default PostReplyInput;
